import React, { useState } from 'react';
import './ExchangeCard.css';
import { ReactComponent as ExternalLinkIcon } from '../assets/icons/external-link.svg';

const formatPersianNumber = (value, options = {}) => (
  new Intl.NumberFormat('fa-IR', options).format(value)
);

const ExchangeCard = ({ exchange, rank }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCtaClick = (e) => {
    if (isLoading) {
      e.preventDefault();
      return;
    }

    // Track exchange click event for GTM
    if (exchange.exchangeSlug && window.trackBuyListingExchangeClick) {
      window.trackBuyListingExchangeClick(exchange.exchangeSlug);
    }

    if (!exchange.website || exchange.website === '#') {
      e.preventDefault();
    } else {
      setIsLoading(true);
      // Reset loading state after 3 seconds in case user returns to this tab
      setTimeout(() => setIsLoading(false), 3000);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Extract deposit speed from badges
  const depositSpeedBadge = exchange.badges?.find(badge => badge.type === 'deposit-speed');
  const depositSpeedValue = depositSpeedBadge?.text?.replace('سرعت واریز: ', '').replace(' دقیقه', '') || '2';
  const depositSpeedNumber = Number(depositSpeedValue);
  const depositSpeed = Number.isFinite(depositSpeedNumber)
    ? formatPersianNumber(depositSpeedNumber)
    : depositSpeedValue;

  // Format volume
  const formatVolume = (volume) => {
    if (!volume || volume === 0) return null;

    if (volume >= 1000000000) {
      return { number: formatPersianNumber(volume / 1000000000, { maximumFractionDigits: 1 }), unit: 'میلیارد دلار' };
    }
    if (volume >= 1000000) {
      return { number: formatPersianNumber(volume / 1000000, { maximumFractionDigits: 1 }), unit: 'میلیون دلار' };
    }
    if (volume >= 1000) {
      return { number: formatPersianNumber(volume / 1000, { maximumFractionDigits: 1 }), unit: 'هزار دلار' };
    }

    return { number: formatPersianNumber(volume), unit: 'دلار' };
  };

  const volumeDetails = formatVolume(exchange.dailyVolume);

  // Format fee
  const feeText = formatPersianNumber(exchange.avgFee || 0.05, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Get primary badge (best-price or first badge)
  const primaryBadge = exchange.badges?.find(badge => badge.type === 'best-price') || exchange.badges?.[0];
  const hasExpandableDetails = true; // All cards can expand
  const cardTitleId = `exchange-${exchange.id}-title`;
  const detailsId = `exchange-${exchange.id}-details`;

  return (
    <article className={`exchange-card ${isExpanded ? 'open' : ''}`} aria-labelledby={cardTitleId}>
      <div className="card-top">
        {/* Header Row: Pair Info (Left) and Exchange Info (Right) */}
        <div className="card-header-row">
          {/* Pair Info - Left Side */}
          {exchange.baseSymbol && exchange.quoteSymbol && exchange.price ? (
            <div className="pair-info">
              <div className="coin-icon">
                {exchange.quoteLogo && (
                  <img
                    src={exchange.quoteLogo}
                    alt=""
                    className="coin-logo-back"
                  />
                )}
                {exchange.baseLogo ? (
                  <img
                    src={exchange.baseLogo}
                    alt=""
                    className="coin-logo-front"
                  />
                ) : (
                  <div className="coin-icon-placeholder coin-logo-front"></div>
                )}
              </div>
              <span className="pair-name">
                {exchange.baseSymbol} / {exchange.quoteSymbol}
              </span>
            </div>
          ) : null}

          {/* Exchange Info - Right Side */}
          <div className="exchange-info">
            {/* Exchange logo */}
            <div className="exchange-logo-wrapper">
              {exchange.logo && exchange.logo.startsWith('http') ? (
                <img
                  src={exchange.logo}
                  alt=""
                  className="exchange-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                className="exchange-logo-fallback"
                aria-hidden="true"
                style={{
                  backgroundColor: exchange.logoBg || '#6b7280',
                  display: exchange.logo && exchange.logo.startsWith('http') ? 'none' : 'flex'
                }}
              >
                {exchange.logo || exchange.name?.charAt(0)}
              </div>
            </div>
            {/* Exchange name */}
            <h2 className="exchange-name" id={cardTitleId}>{exchange.name}</h2>
            {/* Tag appears last */}
            {primaryBadge && (
              <span className={`tag ${primaryBadge.type}`}>
                {primaryBadge.text}
              </span>
            )}
          </div>
        </div>

        {/* Price Section - Centered */}
        {exchange.baseSymbol && exchange.priceSymbol && exchange.price ? (
          <div className="price-section">
            <span className="price-value">{exchange.price}</span>
            <span className="currency">{exchange.priceSymbol}</span>
          </div>
        ) : null}

        {/* Action Button */}
        <a
          href={exchange.website || '#'}
          target={exchange.website && exchange.website !== '#' ? "_blank" : "_self"}
          rel="noopener noreferrer"
          className={`action-btn ${isLoading ? 'loading' : ''}`}
          onClick={handleCtaClick}
          aria-label={exchange.website && exchange.website !== '#'
            ? `شروع معامله در ${exchange.name} (در برگه جدید)`
            : `شروع معامله در ${exchange.name}`}
          aria-busy={isLoading}
        >
          <div className="btn-content">
            <ExternalLinkIcon aria-hidden="true" focusable="false" className="external-icon" />
            شروع معامله
          </div>
          {isLoading && (
            <div className="btn-loader" aria-hidden="true">
              <div className="btn-spinner" />
            </div>
          )}
        </a>
      </div>

      {/* Expandable Footer */}
      {hasExpandableDetails ? (
        <div className="card-footer">
          <div
            id={detailsId}
            className={`details-container ${isExpanded ? 'expanded' : ''}`}
            aria-hidden={!isExpanded}
          >
            <div className="details-grid">
              <div className="detail-row">
                <span className="detail-value">
                  <bdi>{feeText}</bdi>
                  <span>٪</span>
                </span>
                <span className="detail-label">کارمزد</span>
              </div>
              <div className="detail-row">
                <span className="detail-value">
                  <bdi>{depositSpeed}</bdi>
                  <span>دقیقه</span>
                </span>
                <span className="detail-label">سرعت واریز</span>
              </div>
              <div className="detail-row">
                <span className="detail-value">
                  {volumeDetails ? (
                    <>
                      <bdi>{volumeDetails.number}</bdi>
                      <span>{volumeDetails.unit}</span>
                    </>
                  ) : 'نامشخص'}
                </span>
                <span className="detail-label">حجم معاملات</span>
              </div>
            </div>
          </div>
          <button
            className="toggle-btn"
            onClick={toggleExpand}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
          >
            <svg aria-hidden="true" className="btn-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={isExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
            </svg>
            <span className="btn-text">{isExpanded ? 'بستن' : 'اطلاعات بیشتر'}</span>

          </button>
        </div>
      ) : (
        <div className="card-footer plain"></div>
      )}
    </article>
  );
};

export default ExchangeCard;
